import { Card, LineChart, type ChartMarker } from '@servicecompressionllc/sc-design-system';
import { formatMonthName, formatPercent, formatUsd, formatUsdWhole } from '../../lib/format.js';
import type { UsageSummaryResponse } from '../../lib/types.js';
import { COST_MARKERS, previousPhrase } from './usageLabels.js';
import { labeledTrend } from './usageTrend.js';
import type { UsagePeriod } from './useUsageLogic.js';

const cardLabel = 'text-xs font-medium uppercase tracking-[0.025em] text-ink-subtle';
const headline = 'text-[1.875rem] font-bold tracking-tight tabular-nums text-ink';

/** The month before a "YYYY-MM" key, as a key. */
const monthBefore = (key: string): string => {
  const [y = 1970, m = 1] = key.split('-').map(Number);
  return m === 1 ? `${String(y - 1)}-12` : `${String(y)}-${String(m - 1).padStart(2, '0')}`;
};

export const MonthCard = ({ month }: { month: UsageSummaryResponse['month'] }) => {
  const lastMonth = formatMonthName(monthBefore(month.month));
  const ratio =
    month.previousMonthToDateUsd > 0 ? month.spentUsd / month.previousMonthToDateUsd : null;
  // The bar runs to whichever is furthest: this month's projection or last month's total.
  const scale = Math.max(month.projectedUsd, month.previousMonthUsd, month.spentUsd, 0.01);
  const at = (usd: number) => `${String(Math.min(100, (usd / scale) * 100))}%`;
  return (
    <Card padding="md">
      <div className="flex flex-col gap-2.5">
        <div className={cardLabel}>{formatMonthName(month.month)} so far</div>
        <div className="flex flex-wrap items-baseline gap-2">
          <span className={headline}>{formatUsdWhole(month.spentUsd)}</span>
          <span className="text-sm text-ink-muted">
            {lastMonth} was {formatUsdWhole(month.previousMonthUsd)}
          </span>
        </div>
        <div className="flex flex-col gap-1.5">
          <div
            className="relative h-2.5 overflow-hidden rounded-full bg-surface"
            role="img"
            aria-label={`${formatUsdWhole(month.spentUsd)} spent, projected ${formatUsdWhole(month.projectedUsd)}; ${lastMonth} was ${formatUsdWhole(month.previousMonthUsd)}`}
          >
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-chart-1 opacity-30"
              style={{ width: at(month.projectedUsd) }}
            />
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-chart-1"
              style={{ width: at(month.spentUsd) }}
            />
            {month.previousMonthUsd > 0 && (
              <div
                className="absolute inset-y-0 w-0.5 bg-ink"
                style={{ left: `calc(${at(month.previousMonthUsd)} - 1px)` }}
              />
            )}
          </div>
          <div
            className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-subtle"
            aria-hidden="true"
          >
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm bg-chart-1" />
              Spent
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm bg-chart-1 opacity-30" />
              Projected
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-3 w-0.5 bg-ink" />
              {lastMonth} total
            </span>
          </div>
        </div>
        <div className="flex flex-wrap justify-between gap-2 text-xs text-ink-subtle">
          <span>
            Projected month end{' '}
            <strong className="font-semibold text-ink">{formatUsdWhole(month.projectedUsd)}</strong>{' '}
            at this pace
          </span>
          <span>
            Day {month.dayOfMonth} of {month.daysInMonth}
          </span>
        </div>
        {ratio !== null && Math.abs(ratio - 1) >= 0.005 && (
          // Neutral either way: total spend grows with adoption, so its pace isn't good or bad.
          <div className="text-xs text-ink-subtle">
            {ratio >= 2
              ? `About ${ratio.toFixed(1)}× ${lastMonth}’s pace`
              : `Pace is ${formatPercent(Math.abs(ratio - 1))} ${ratio < 1 ? 'below' : 'above'} ${lastMonth}`}
          </div>
        )}
      </div>
    </Card>
  );
};

/** "down from $0.62 a month ago": green when the efficiency figure improved, soft red when it got worse. */
const Change = ({
  now,
  before,
  format,
  better,
  phrase,
}: {
  now: number | null;
  before: number | null;
  format: (n: number) => string;
  better: 'lower' | 'higher';
  phrase: string;
}) => {
  if (now === null || before === null || format(now) === format(before)) return null;
  const fell = now < before;
  const improved = better === 'lower' ? fell : !fell;
  return (
    <span className={improved ? 'text-sm text-success' : 'text-sm text-danger'}>
      {[fell ? 'down' : 'up', 'from', format(before), phrase].filter(Boolean).join(' ')}
    </span>
  );
};

interface TrendCardProps {
  data: UsageSummaryResponse;
  period: UsagePeriod;
}

/** The trend rows the period reads in, labeled for a compact axis. */
const trendRows = ({ data, period }: TrendCardProps) => {
  const hourly = period.kind === 'preset' && period.period === '24h';
  return labeledTrend(hourly ? data.byHour : data.byDay, hourly);
};

export const CostPerAnswerCard = ({ data, period }: TrendCardProps) => {
  const rows = trendRows({ data, period })
    .filter((r) => r.costPerAnswerUsd !== null)
    .map((r) => ({ date: r.date, label: r.label, cost: r.costPerAnswerUsd ?? 0 }));
  const markers: ChartMarker[] = COST_MARKERS.flatMap((m) => {
    const row = rows.find((r) => r.date === m.date);
    return row ? [{ x: row.label, label: m.label }] : [];
  });
  const { costPerAnswerUsd } = data.totals;
  return (
    <Card padding="md">
      <div className="flex flex-col gap-2.5">
        <div className={cardLabel}>Cost per answer</div>
        <div className="flex flex-wrap items-baseline gap-2">
          <span className={headline}>
            {costPerAnswerUsd === null ? 'No answers' : formatUsd(costPerAnswerUsd)}
          </span>
          <Change
            now={costPerAnswerUsd}
            before={data.previous.totals.costPerAnswerUsd}
            format={formatUsd}
            better="lower"
            phrase={previousPhrase(period)}
          />
        </div>
        <LineChart
          data={rows}
          xKey="label"
          yKey="cost"
          color="var(--chart-1)"
          markers={markers}
          height={120}
          hideXAxis
          formatValue={formatUsd}
          ariaLabel="Cost per answer over the period"
          emptyMessage="No answers in this period."
        />
      </div>
    </Card>
  );
};

export const CacheShareCard = ({ data, period }: TrendCardProps) => {
  const rows = trendRows({ data, period })
    .filter((r) => r.cacheShare !== null)
    .map((r) => ({ label: r.label, share: Math.round((r.cacheShare ?? 0) * 100) }));
  const { cacheShare } = data.totals;
  return (
    <Card padding="md">
      <div className="flex flex-col gap-2.5">
        <div className={cardLabel}>Input served from cache</div>
        <div className="flex flex-wrap items-baseline gap-2">
          <span className={headline}>
            {cacheShare === null ? 'No input' : formatPercent(cacheShare)}
          </span>
          <Change
            now={cacheShare}
            before={data.previous.totals.cacheShare}
            format={formatPercent}
            better="higher"
            phrase=""
          />
        </div>
        <LineChart
          data={rows}
          xKey="label"
          yKey="share"
          color="var(--chart-2)"
          height={120}
          hideXAxis
          formatValue={(n) => `${String(n)}%`}
          ariaLabel="Share of input served from cache over the period"
          emptyMessage="No chat input in this period."
        />
        <p className="text-xs text-ink-subtle">
          A drop here means something stopped reusing cached history. Healthy is above 80%.
        </p>
      </div>
    </Card>
  );
};
